import { useEffect, useState } from 'react';
import { Briefcase, Sparkles, Users } from 'lucide-react';
import { Modale } from '../ui/Modale';
import { BoutonMint, BoutonSecondaire, Chargement, MessageErreur, Pastille } from '../ui/Primitives';
import { BandeauExpedition } from '../notifications/BandeauExpedition';
import { ErreurApi } from '../../lib/api';
import {
  useApercuDiffusion,
  useDiffuserOffres,
  type OffreProposee,
  type PersonneEcartee,
  type ResultatDiffusion,
} from '../../lib/usePedagogique';

function dateCourte(valeur: string | null): string {
  if (!valeur) return '—';
  return new Date(valeur).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

function pluriel(n: number, singulier: string, plurielForme = `${singulier}s`): string {
  return `${n} ${n > 1 ? plurielForme : singulier}`;
}

/**
 * Diffusion d'offres d'entreprise à la sélection (CDC §6.9).
 *
 * L'API dit qui recevra, qui est écarté et pourquoi, et quelles offres sont
 * ouvertes sur l'antenne — dans l'ordre du matching : les besoins qu'il
 * propose déjà à ces personnes d'abord. L'écran choisit, il ne recalcule rien.
 */
export function ModaleDiffusionOffres({
  apprenantIds,
  ouverte,
  onFermer,
  onDiffuse,
}: {
  apprenantIds: string[];
  ouverte: boolean;
  onFermer: () => void;
  onDiffuse: (resultat: ResultatDiffusion) => void;
}): JSX.Element | null {
  const { data, isLoading, error } = useApercuDiffusion(apprenantIds, ouverte);
  const diffuser = useDiffuserOffres();
  const [choisies, setChoisies] = useState<string[]>([]);
  const [sms, setSms] = useState(false);

  useEffect(() => {
    if (!ouverte) {
      setChoisies([]);
      diffuser.reset();
    }
    // `diffuser` change à chaque rendu : seule l'ouverture compte.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouverte]);

  useEffect(() => {
    if (data) setSms(data.expedition.sms);
  }, [data]);

  const maxOffres = data?.maxOffres ?? 5;
  const plafond = choisies.length >= maxOffres;

  function basculer(offre: OffreProposee): void {
    setChoisies((liste) =>
      liste.includes(offre.besoinId)
        ? liste.filter((id) => id !== offre.besoinId)
        : [...liste, offre.besoinId],
    );
  }

  function envoyer(): void {
    diffuser.mutate(
      { apprenantIds, besoinIds: choisies, sms },
      { onSuccess: (resultat) => onDiffuse(resultat) },
    );
  }

  const destinataires = data?.destinataires ?? 0;

  return (
    <Modale
      titre="Diffuser des offres à la sélection"
      sousTitre={`${pluriel(apprenantIds.length, 'personne sélectionnée', 'personnes sélectionnées')} · ${maxOffres} offres au plus par envoi`}
      ouverte={ouverte}
      onFermer={onFermer}
      largeur="max-w-3xl"
    >
      <div className="space-y-4 p-4 sm:p-6">
        {isLoading && <Chargement libelle="Recherche des offres ouvertes…" />}
        {error && (
          <MessageErreur
            message={error instanceof ErreurApi ? error.message : 'Les offres n’ont pas pu être chargées.'}
          />
        )}

        {data && (
          <>
            <BandeauExpedition
              etat={data.expedition}
              contexte="La diffusion est tracée personne par personne, même sans envoi."
            />

            <div className="flex flex-wrap items-center gap-2 text-sm text-codialis-navy">
              <Users className="h-4 w-4 text-codialis-gray" aria-hidden="true" />
              <span>
                <strong>{pluriel(destinataires, 'destinataire')}</strong>
                {data.ecartes.length > 0 && (
                  <span className="text-codialis-gray"> · {pluriel(data.ecartes.length, 'personne écartée', 'personnes écartées')}</span>
                )}
              </span>
            </div>

            {data.ecartes.length > 0 && <ListeEcartes ecartes={data.ecartes} />}

            <p className="text-xs leading-relaxed text-codialis-gray">
              Chaque personne reçoit un seul message, dans son espace et par e-mail : il nomme
              l’entreprise, sa commune, la formation, les postes et la date souhaitée, et l’invite
              à contacter son conseiller(e) — à défaut son antenne — pour la mise en relation.
              Une offre déjà reçue ne lui est pas renvoyée.
            </p>

            {destinataires === 0 ? (
              <p className="rounded-xl bg-codialis-sand px-4 py-3 text-sm text-codialis-navy">
                Personne dans la sélection ne peut recevoir d’offre : voir les motifs ci-dessus.
              </p>
            ) : data.offres.length === 0 ? (
              <p className="rounded-xl bg-codialis-sand px-4 py-3 text-sm text-codialis-navy">
                Aucun besoin d’entreprise ouvert sur l’antenne de la sélection. Les besoins se
                recueillent dans « Entreprises ».
              </p>
            ) : (
              <ul className="space-y-2">
                {data.offres.map((offre) => {
                  const coche = choisies.includes(offre.besoinId);
                  const bloquee = offre.refus !== null || (!coche && plafond);
                  return (
                    <li key={offre.besoinId}>
                      <label
                        className={`flex min-w-0 gap-3 rounded-xl border px-3 py-3 transition sm:px-4 ${
                          coche ? 'border-codialis-mint bg-codialis-mint/10' : 'border-gray-200'
                        } ${bloquee ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:bg-gray-50'}`}
                      >
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 shrink-0 rounded"
                          checked={coche}
                          disabled={bloquee}
                          onChange={() => basculer(offre)}
                        />
                        <OffreLue offre={offre} />
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}

            {destinataires > 0 && data.offres.length > 0 && (
              <label className="flex min-h-[40px] items-start gap-3 text-sm text-codialis-navy">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded"
                  checked={sms}
                  onChange={() => setSms((valeur) => !valeur)}
                />
                <span>
                  Prévenir aussi par SMS
                  <span className="block text-xs text-codialis-gray">
                    {data.expedition.sms
                      ? 'Un SMS court renvoie à l’e-mail : la liste des offres y coûterait plusieurs crédits.'
                      : 'Le SMS n’est pas raccordé : il sera composé et tracé, sans partir.'}
                  </span>
                </span>
              </label>
            )}

            {diffuser.error && (
              <MessageErreur
                message={
                  diffuser.error instanceof ErreurApi
                    ? diffuser.error.message
                    : 'La diffusion n’a pas abouti.'
                }
              />
            )}
          </>
        )}
      </div>

      <div className="flex flex-col-reverse gap-2 border-t border-gray-100 px-4 py-3.5 sm:flex-row sm:justify-end sm:px-6">
        <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
        <BoutonMint
          onClick={envoyer}
          disabled={choisies.length === 0 || destinataires === 0 || diffuser.isPending}
        >
          {diffuser.isPending
            ? 'Diffusion…'
            : choisies.length === 0
              ? 'Choisir au moins une offre'
              : `Diffuser ${pluriel(choisies.length, 'offre')} à ${pluriel(destinataires, 'personne')}`}
        </BoutonMint>
      </div>
    </Modale>
  );
}

function OffreLue({ offre }: { offre: OffreProposee }): JSX.Element {
  return (
    <div className="min-w-0 flex-1 space-y-1">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Briefcase className="h-4 w-4 shrink-0 text-codialis-gray" aria-hidden="true" />
        <span className="font-semibold text-codialis-navy">{offre.entreprise}</span>
        {offre.ville && <span className="text-xs text-codialis-gray">{offre.ville}</span>}
        <span className="text-xs text-codialis-gray">· {offre.siteCodes.join(', ')}</span>
      </div>
      <div className="text-xs text-codialis-gray">
        {offre.formation ?? 'Formation non précisée'} ·{' '}
        {offre.placesRestantes} poste{offre.placesRestantes > 1 ? 's' : ''} libre
        {offre.placesRestantes > 1 ? 's' : ''} sur {offre.nombrePostes}
        {offre.dateSouhaitee ? ` · à partir du ${dateCourte(offre.dateSouhaitee)}` : ''}
      </div>
      {offre.profilRecherche && (
        <div className="break-words text-xs text-codialis-navy">Profil recherché : {offre.profilRecherche}</div>
      )}
      <div className="flex flex-wrap gap-1.5 pt-0.5">
        {offre.proposees > 0 && (
          <span className="inline-flex items-center gap-1">
            <Sparkles className="h-3.5 w-3.5 text-codialis-sky" aria-hidden="true" />
            <Pastille
              texte={`Matching : ${offre.proposees} de la sélection${offre.meilleurScore !== null ? ` · ${offre.meilleurScore} %` : ''}`}
              ton="info"
            />
          </span>
        )}
        {offre.memeFormation > 0 && (
          <Pastille texte={`${offre.memeFormation} de la même formation`} ton="mint" />
        )}
        {offre.dejaRecue > 0 && <Pastille texte={`Déjà reçue par ${offre.dejaRecue}`} ton="neutral" />}
        {offre.ouvertRqth && <Pastille texte="Ouverte RQTH" ton="success" />}
        {offre.refus === null && offre.aEnvoyer > 0 && (
          <Pastille
            texte={`Partira à ${offre.aEnvoyer} personne${offre.aEnvoyer > 1 ? 's' : ''}`}
            ton="neutral"
          />
        )}
      </div>
      {offre.refus && <div className="text-xs text-amber-700">{offre.refus}</div>}
    </div>
  );
}

function ListeEcartes({ ecartes }: { ecartes: PersonneEcartee[] }): JSX.Element {
  return (
    <details className="rounded-xl border border-gray-200 px-4 py-2 text-sm">
      <summary className="flex min-h-[40px] cursor-pointer items-center font-semibold text-codialis-navy">
        Voir les personnes écartées
      </summary>
      <ul className="space-y-1 pb-2 text-xs">
        {ecartes.map((ligne) => (
          <li key={ligne.apprenantId} className="flex flex-wrap justify-between gap-2">
            <span className="text-codialis-navy">{ligne.apprenant}</span>
            <span className="text-codialis-gray">{ligne.motif}</span>
          </li>
        ))}
      </ul>
    </details>
  );
}
